import RvHookupIcon from '@mui/icons-material/RvHookup';
import AuthContext from '../contexts/authentication/AuthProvider';
import { useContext } from 'react';
import {
  ArticleIcon,
  //AssignmentIndIcon,
  CalendarViewDayIcon,
  CalendarViewMonthIcon,
  CloudSyncIcon,
  ContactMailIcon,
  ContactPageIcon,
  //ContentPasteSearchIcon,
  DesktopWindowsRoundedIcon,
  EngineeringIcon,
  EventNoteIcon,
  GroupsIcon,
  LiveHelpIcon,
  MailIcon,
  PedalBikeIcon,
  PeopleIcon,
  PersonIcon,
  StorageIcon,
  TuneIcon,
  UploadFileRoundedIcon,
} from '../icons/material';
import {
  ControlPanelRoute,
  TrackingRoute,
  MailingRoute,
  MaintainerRoute,
  MetbusAppRoute,
  RosterRoute,
  UserManagerRoute,
  MetbusSystems,
} from '../types/appRoutes';
import { SystemClaimPermission } from '../types/claims/claimsApp';
import {
  MinimumPermission,
  PermissionLevel,
} from '../types/claims/profilePermission';
import { SystemModules } from '../types/claims/userManagerModules';
import { selectPermissions } from '../utils/selectPermissions';
import { useDocumentPermissions } from './useDocumentPermissions';
import { useRoleDescriptionPermissions } from './useRoleDescriptionPermissions';

export interface AppModule<T> {
  title: string;
  route: T;
  icon: JSX.Element;
  minimunPermission?: MinimumPermission;
}
export interface AppSection<T> {
  title: string;
  icon: JSX.Element;
  modules: AppModule<T>[];
}
interface SidebarOption {
  metbusApps: AppModule<MetbusAppRoute>;
  metbusDocs: AppModule<MetbusAppRoute>;
  roster?: AppSection<RosterRoute>;
  trackings?: AppSection<TrackingRoute>;
  maintainers?: AppSection<MaintainerRoute>;
  control?: AppSection<ControlPanelRoute>;
  userManager?: AppSection<UserManagerRoute>;
  mailing?: AppSection<MailingRoute>;
  metbusSystems?: AppSection<MetbusSystems>;
}

const modulesMap = new Map<
  | RosterRoute
  | TrackingRoute
  | MaintainerRoute
  | ControlPanelRoute
  | MailingRoute
  | UserManagerRoute,
  SystemModules
>([
  ['/roster/vista-semanal', 'RosterVistaSemanal'],
  ['/roster/vista-mensual', 'RosterVistaMensual'],
  ['/roster/carga-de-programacion', 'RosterCargaDeProgramacion'],
  ['/roster/resumen-trabajado', 'RosterResumenTrabajado'],
  ['/seguimiento/intercambios', 'Exchanges'],
  ['/seguimiento/alta-medica', 'MedicalDischarge'],
  ['/mantenedores/feriados', 'HolidayMaintainer'],
  // ['/control/historial-procesos', 'ControlTaskHistory'],
  ['/control/actualizaciones', 'ControlUpdater'],
  ['/control/super-asistencia', 'ControlUpdater'],
  ['/user-manager/usuarios', 'UserMaintainer'],
  ['/user-manager/sistemas', 'SystemMaintainer'],
  ['/user-manager/perfiles', 'ProfileMaintainer'],
  ['/mailing/acciones', 'ActionMaintainer'],
  ['/mailing/destinatarios', 'RecipientMaintainer'],
  ['/mailing/grupos', 'GroupMaintainer'],
  ['/mailing/configuraciones', 'ConfigurationMaintainer'],
]);

export const useSidebarSections = (): SidebarOption => {
  const { user } = useContext(AuthContext);
  const { areas } = useDocumentPermissions();
  const { areas: roles } = useRoleDescriptionPermissions();
  const metbusApps: SidebarOption['metbusApps'] = {
    title: 'Aplicaciones Metbus',
    icon: <LiveHelpIcon />,
    route: '/aplicaciones-metbus',
  };

  const metbusDocs: SidebarOption['metbusApps'] = {
    title: 'Documentación',
    icon: <ArticleIcon />,
    route: '/docs',
  };

  if (!user?.claims)
    return {
      metbusApps,
      metbusDocs,
    };

  const systems: SidebarOption['metbusSystems'] = {
    title: 'Sistemas',
    icon: <EventNoteIcon />,
    modules: [],
  };

  const procedimientos: AppModule<MetbusSystems> = {
    title: 'Procedimientos',
    route: '/procedimientos',
    icon: <CalendarViewDayIcon />,
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const roleDescription: AppModule<MetbusSystems> = {
    title: 'Descripción de Cargo',
    route: '/roleDocs',
    icon: <CalendarViewDayIcon />,
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const roster: SidebarOption['roster'] = {
    title: 'Asistencia',
    icon: <EventNoteIcon />,
    modules: [],
  };

  const weekView: AppModule<RosterRoute> = {
    title: 'Vista Semanal',
    route: '/roster/vista-semanal',
    icon: <CalendarViewDayIcon />,
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const monthView: AppModule<RosterRoute> = {
    title: 'Vista Mensual',
    route: '/roster/vista-mensual',
    icon: <CalendarViewMonthIcon />,
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const scheduleLoad: AppModule<RosterRoute> = {
    title: 'Carga de Programación',
    route: '/roster/carga-de-programacion',
    icon: <UploadFileRoundedIcon />,
    minimunPermission: MinimumPermission.ReadAndWrite,
  };

  const workedSummary: AppModule<RosterRoute> = {
    title: 'Resumen Trabajado',
    route: '/roster/resumen-trabajado',
    icon: <ContactPageIcon />,
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const rosterModules = [
    weekView,
    monthView,
    scheduleLoad,
    workedSummary,
  ].filter((rosterModule) => {
    const permissionsRequired: PermissionLevel[] | undefined =
      selectPermissions(rosterModule?.minimunPermission);
    return (
      Object.keys(user.claims as SystemClaimPermission) as Array<
        keyof SystemClaimPermission
      >
    ).find((moduleName) => {
      return (
        moduleName === modulesMap.get(rosterModule.route) &&
        (user.claims as SystemClaimPermission)[moduleName] &&
        permissionsRequired?.includes(
          (user.claims as SystemClaimPermission)[moduleName]
        )
      );
    });
  });

  roster.modules = rosterModules;

  const addModules = () => {
    if (areas.length > 0) {
      systems.modules.push(procedimientos);
    }
    if (roles.length > 0) {
      systems.modules.push(roleDescription);
    }
  };

  addModules();

  const trackings: SidebarOption['trackings'] = {
    title: 'Seguimiento',
    icon: <PeopleIcon />,
    modules: [],
  };

  const exchanges: AppModule<TrackingRoute> = {
    title: 'Intercambios entre Terminales',
    icon: <RvHookupIcon />,
    route: '/seguimiento/intercambios',
    minimunPermission: MinimumPermission.ReadOnly,
  };
  const medicalDischarge: AppModule<TrackingRoute> = {
    title: 'Alta Medica',
    icon: <RvHookupIcon />,
    route: '/seguimiento/alta-medica',
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const trackingsModules = [exchanges, medicalDischarge].filter((module) => {
    const permissionsRequired: number[] | undefined = selectPermissions(
      module?.minimunPermission
    );

    return (
      Object.keys(user.claims as SystemClaimPermission) as Array<
        keyof SystemClaimPermission
      >
    ).find(
      (moduleName) =>
        moduleName === modulesMap.get(module.route) &&
        (user.claims as SystemClaimPermission)[moduleName] &&
        permissionsRequired?.includes(
          (user.claims as SystemClaimPermission)[moduleName]
        )
    );
  });

  trackings.modules = trackingsModules;

  const maintainers: SidebarOption['maintainers'] = {
    title: 'Mantenedores',
    icon: <StorageIcon />,
    modules: [],
  };

  const holidays: AppModule<MaintainerRoute> = {
    title: 'Feriados',
    route: '/mantenedores/feriados',
    icon: <PedalBikeIcon />,
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const maintainersModules = [holidays].filter((maintainersModule) => {
    const permissionsRequired: PermissionLevel[] | undefined =
      selectPermissions(maintainersModule?.minimunPermission);

    return (
      Object.keys(user.claims as SystemClaimPermission) as Array<
        keyof SystemClaimPermission
      >
    ).find(
      (moduleName) =>
        moduleName === modulesMap.get(maintainersModule.route) &&
        (user.claims as SystemClaimPermission)[moduleName] &&
        permissionsRequired?.includes(
          (user.claims as SystemClaimPermission)[moduleName]
        )
    );
  });

  maintainers.modules = maintainersModules;

  const controlPanel: SidebarOption['control'] = {
    title: 'Panel de Control',
    icon: <EngineeringIcon />,
    modules: [],
  };

  const dbUpdater: AppModule<ControlPanelRoute> = {
    title: 'Actualizar Base de Datos',
    route: '/control/actualizaciones',
    icon: <CloudSyncIcon />,
    minimunPermission: MinimumPermission.SuperUser,
  };

  const superAttendance: AppModule<ControlPanelRoute> = {
    title: 'Super Asistencia',
    route: '/control/super-asistencia',
    icon: <CloudSyncIcon />,
    minimunPermission: MinimumPermission.SuperUser,
  };

  const controlPanelModules = [dbUpdater, superAttendance].filter(
    (controlPanelModule) => {
      const permissionsRequired: PermissionLevel[] | undefined =
        selectPermissions(controlPanelModule?.minimunPermission);
      return (
        Object.keys(user.claims as SystemClaimPermission) as Array<
          keyof SystemClaimPermission
        >
      ).find(
        (moduleName) =>
          moduleName === modulesMap.get(controlPanelModule.route) &&
          (user.claims as SystemClaimPermission)[moduleName] &&
          permissionsRequired?.includes(
            (user.claims as SystemClaimPermission)[moduleName]
          )
      );
    }
  );

  controlPanel.modules = controlPanelModules;

  const userManager: SidebarOption['userManager'] = {
    title: 'Manejo de Usuarios',
    icon: <PeopleIcon />,
    modules: [],
  };

  const userMaintainer: AppModule<UserManagerRoute> = {
    title: 'Usuarios',
    route: '/user-manager/usuarios',
    icon: <PersonIcon />,
    minimunPermission: MinimumPermission.ReadOnly,
  };

  const systemMaintainer: AppModule<UserManagerRoute> = {
    title: 'Sistemas',
    route: '/user-manager/sistemas',
    icon: <DesktopWindowsRoundedIcon />,
    minimunPermission: MinimumPermission.SuperUser,
  };

  const profileMaintainer: AppModule<UserManagerRoute> = {
    title: 'Perfiles',
    route: '/user-manager/perfiles',
    icon: <TuneIcon />,
    minimunPermission: MinimumPermission.SuperUser,
  };

  const userManagerModules = [
    userMaintainer,
    systemMaintainer,
    profileMaintainer,
  ].filter((userManagerModule) => {
    const permissionsRequired: number[] | undefined = selectPermissions(
      userManagerModule?.minimunPermission
    );
    return (
      Object.keys(user.claims as SystemClaimPermission) as Array<
        keyof SystemClaimPermission
      >
    ).find(
      (moduleName) =>
        moduleName === modulesMap.get(userManagerModule.route) &&
        (user.claims as SystemClaimPermission)[moduleName] &&
        permissionsRequired?.includes(
          (user.claims as SystemClaimPermission)[moduleName]
        )
    );
  });

  userManager.modules = userManagerModules;

  const mailing: SidebarOption['mailing'] = {
    title: 'Manejo de Correos',
    icon: <MailIcon />,
    modules: [],
  };

  // const actionMaintainer: AppModule<MailingRoute> = {
  //   title: 'Acciones',
  //   route: '/mailing/acciones',
  //   icon: <ContentPasteSearchIcon />,
  //   minimunPermission: MinimumPermission.SuperUser,
  // };

  const recipientMaintainer: AppModule<MailingRoute> = {
    title: 'Destinatarios',
    route: '/mailing/destinatarios',
    icon: <ContactMailIcon />,
    minimunPermission: MinimumPermission.SuperUser,
  };

  const groupMaintainer: AppModule<MailingRoute> = {
    title: 'Grupos',
    route: '/mailing/grupos',
    icon: <GroupsIcon />,
    minimunPermission: MinimumPermission.SuperUser,
  };

  // const configurationMaintainer: AppModule<MailingRoute> = {
  //   title: 'Configuraciones',
  //   route: '/mailing/configuraciones',
  //   icon: <AssignmentIndIcon />,
  //   minimunPermission: MinimumPermission.SuperUser,
  // };

  const mailingModules = [
    //actionMaintainer,
    recipientMaintainer,
    groupMaintainer,
    //configurationMaintainer,
  ].filter((mailingModule) => {
    const permissionsRequired: number[] | undefined = selectPermissions(
      mailingModule?.minimunPermission
    );
    return (
      Object.keys(user.claims as SystemClaimPermission) as Array<
        keyof SystemClaimPermission
      >
    ).find(
      (moduleName) =>
        moduleName === modulesMap.get(mailingModule.route) &&
        (user.claims as SystemClaimPermission)[moduleName] &&
        permissionsRequired?.includes(
          (user.claims as SystemClaimPermission)[moduleName]
        )
    );
  });

  mailing.modules = mailingModules;

  return {
    metbusApps,
    metbusDocs,
    metbusSystems: systems.modules.length > 0 ? systems : undefined,
    roster: roster.modules.length > 0 ? roster : undefined,
    trackings: trackings.modules.length > 0 ? trackings : undefined,
    maintainers: maintainers.modules.length > 0 ? maintainers : undefined,
    control: controlPanel.modules.length > 0 ? controlPanel : undefined,
    // userManager: userManager.modules.length > 0 ? userManager : undefined,
    // mailing: mailing.modules.length > 0 ? mailing : undefined,
  };
};
